<template>
  <div :class="[{ 'video-player-wrapper': !isEmbed }]">
    <iframe
      v-if="isEmbed"
      :src="iframeSrc"
      frameborder="0"
      allowfullscreen="true"
      mozallowfullscreen="true"
      webkitallowfullscreen="true"
      @mouseleave="handleTriggerFocus"
      @click.stop
    ></iframe>
    <NativeVideoPlayer
      v-else
      :blob="blob"
      :url="url"
      :controls="controls"
      :autoplay="autoplay"
      :muted="muted"
      :play-on-hover="playOnHover"
      :ratio="ratio"
      :thumb-url="thumbUrl"
      :compact="compact"
      :video-id="videoId"
      :ui-styling="uiStyling"
      :pause-when-not-visible
      @timeupdate="$event => emit('timeupdate', $event)"
      @playing="() => emit('playing')"
      @pause="() => emit('pause')"
      @ended="() => emit('ended')"
      @error="() => emit('error')"
      @video-clicked="() => emit('video-clicked')"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted } from 'vue';
import type { UiStyling } from '@getaccept/lib-shared/src/branding/types/ui-styling';
import * as VideoLinkHelper from '../../link/helpers/video-link.helper';
import NativeVideoPlayer from './NativeVideoPlayer.vue';

const {
  blob = null,
  url = null,
  controls = true,
  autoplay = false,
  muted = false,
  playOnHover = false,
  ratio = '16 / 9',
  thumbUrl = null,
  compact = false,
  videoId = null,
  uiStyling = null,
  pauseWhenNotVisible = false,
} = defineProps<{
  blob?: Blob | null;
  url?: string | null;
  controls?: boolean;
  autoplay?: boolean;
  muted?: boolean;
  playOnHover?: boolean;
  ratio?: string;
  thumbUrl?: string | null;
  compact?: boolean;
  videoId?: string | null;
  uiStyling?: UiStyling | null;
  pauseWhenNotVisible?: boolean;
}>();

const emit = defineEmits<{
  timeupdate: [number];
  playing: [];
  pause: [];
  ended: [];
  error: [];
  'video-clicked': [];
  'iframe-clicked': [];
}>();

const isLoomEmbed = computed(() => (url ? VideoLinkHelper.isLoomUrl(url) : false));
const isVidyardEmbed = computed(() => (url ? VideoLinkHelper.isVidyardUrl(url) : false));
const isWistiaEmbed = computed(() => (url ? VideoLinkHelper.isWistiaUrl(url) : false));
const isEmbed = computed(() => isLoomEmbed.value || isVidyardEmbed.value || isWistiaEmbed.value);
const iframeSrc = computed(() => {
  if (!url) return '';
  if (isLoomEmbed.value) {
    return VideoLinkHelper.getLoomEmbedUrl(url);
  }
  if (isVidyardEmbed.value) {
    return VideoLinkHelper.getVidyardEmbedUrl(url);
  }
  if (isWistiaEmbed.value) {
    return VideoLinkHelper.getWistiaEmbedUrl(url);
  }
  return '';
});

const handleIframeBlur = () => {
  emit('iframe-clicked');
};

const handleTriggerFocus = () => {
  window.focus();
};

onMounted(() => {
  if (isEmbed.value) {
    window.addEventListener('blur', handleIframeBlur);
  }
});

onBeforeUnmount(() => {
  if (isEmbed.value) {
    window.removeEventListener('blur', handleIframeBlur);
  }
});
</script>

<style lang="scss" scoped>
.video-player-wrapper {
  height: 100%;
  width: 100%;
  overflow: hidden;
}
</style>
